import { useLazyQuery } from '@apollo/client';
import { map } from 'lodash';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CACHE_REVALIDATION_TIME,
  MATOMO_CATEGORY,
  PAGE_TYPE,
  SESSION_STATUS,
  TRACK_VALUE
} from '../../../common/constants';
import useTrackPageEvent from '../../../common/useTrackPageEvent';
import useWatchTime from '../../../common/useWatchTime';
import MODULES, {
  getMetaOgImage,
  getUniqueVideoPodcastIds
} from '../../../common/utils';
import CustomMeta from '../../../components/CustomMeta';
import ModuleComponent from '../../../components/ModuleComponents';
import { GET_PAGE_V2, PUBLISHED_PAGES } from '../../../graphql/Queries';
import client from '../../../lib/apollo';

const MetaFooter = dynamic(() => import('../../../components/MetaFooter'), {
  loading: () => null,
  ssr: false
});

function Topic({
  page: {
    modules: pageData = [],
    title,
    description,
    slug,
    metaImage,
    metaHeader,
    metaFooter,
    id: pageId,
    type
  } = {}
}) {
  const { data, status } = useSession();
  const [modules, setModules] = useState(() =>
    pageData?.map((module) => ({
      ...MODULES[module?.type]?.(module),
      isLoading: !!module?.showSkeleton
    }))
  );

  const [fetchPage] = useLazyQuery(GET_PAGE_V2, {
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        token: data?.accessToken
      }
    },
    onCompleted: (page) => {
      if (page) {
        const moduleObject = page?.pageV2?.modules?.reduce((acc, module) => {
          acc[module?.id] = {
            ...MODULES[module?.type]?.(module)
          };
          return acc;
        }, {});
        setModules((prev) =>
          prev
            .filter(({ id, isLoading }) => !(isLoading && !moduleObject?.[id]))
            .map((module) => ({
              ...module,
              ...moduleObject?.[module?.id],
              isLoading: false
            }))
        );
      }
    },
    onError: () => {}
  });

  const permissionBasedModulesIds = useMemo(
    () =>
      pageData
        ?.filter((module) => !!module?.showSkeleton)
        ?.map((module) => module?.id),
    [pageData]
  );

  useTrackPageEvent({
    id: pageId,
    type,
    slug,
    title,
    category: MATOMO_CATEGORY.VIEW_DETAIL,
    statusSuccess: TRACK_VALUE.SUCCESS
  });

  useEffect(() => {
    setModules(
      pageData?.map((module) => {
        return {
          ...MODULES[module?.type]?.(module),
          isLoading: !!module?.showSkeleton
        };
      })
    );
  }, [pageData]);

  useEffect(() => {
    if (
      status !== SESSION_STATUS.LOADING &&
      permissionBasedModulesIds.length > 0
    ) {
      setModules(
        pageData?.map((module) => ({
          ...MODULES[module?.type]?.(module),
          isLoading: !!module?.showSkeleton
        }))
      );
      fetchPage({
        variables: {
          where: {
            type: PAGE_TYPE?.TOPIC,
            slug
          },
          filter: {
            moduleIds: permissionBasedModulesIds
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, permissionBasedModulesIds, fetchPage, pageData, slug]);

  const { videoIds, podcastIds } = useMemo(
    () => getUniqueVideoPodcastIds(modules),
    [modules]
  );
  const { videosWatchTimes, podcastListenTimes } = useWatchTime(
    videoIds,
    podcastIds
  );
  const meta = metaHeader || '';

  return (
    <>
      <CustomMeta
        title={title}
        description={description}
        image={getMetaOgImage('topics', slug) || metaImage}
        meta={meta}
      />
      {modules?.map((module) => (
        <ModuleComponent
          key={module?.id}
          videosWatchTimes={videosWatchTimes}
          podcastListenTimes={podcastListenTimes}
          {...module}
        />
      ))}
      <MetaFooter data={metaFooter} />
    </>
  );
}

export async function getStaticPaths() {
  try {
    const res = await client.query({
      query: PUBLISHED_PAGES,
      fetchPolicy: 'no-cache',
      variables: {
        where: {
          type: PAGE_TYPE?.TOPIC
        }
      }
    });
    const slugs = res?.data?.publishedPages?.slugs;

    if (!slugs) return { paths: [], fallback: 'blocking' };

    return {
      paths:
        map(slugs, (slug) => {
          return { params: { slug } };
        }) || [],
      fallback: 'blocking'
    };
  } catch (error) {
    return { paths: [], fallback: 'blocking' };
  }
}

export async function getStaticProps(ctx) {
  const configs = {
    revalidate: CACHE_REVALIDATION_TIME
  };

  try {
    const {
      data: { pageV2: page }
    } = await client.query({
      query: GET_PAGE_V2,
      fetchPolicy: 'no-cache',
      variables: {
        where: {
          type: PAGE_TYPE?.TOPIC,
          slug: ctx?.params?.slug?.toLowerCase()
        },
        filter: {
          skipPermissionModules: true
        }
      },
      context: {
        headers: {
          'rate-limit-bypass-secret': process.env.RATE_LIMIT_BYPASS_SECRET
        }
      }
    });
    if (!page)
      return {
        notFound: true,
        ...configs
      };
    return {
      props: { page },
      ...configs
    };
  } catch (error) {
    return {
      notFound: true,
      ...configs
    };
  }
}

export default Topic;
